import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { IoLocation } from 'react-icons/io5';
import useAuthStore from '../../store/authStore';

const DynamicKakaoMapSelectContainer = forwardRef(({ 
    initialLatitude,
    initialLongitude,
    className = "w-full h-full",
    fallbackClassName = "w-full h-full bg-gray-200 flex items-center justify-center"
}, ref) => {
    const { member } = useAuthStore();
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);

    // 위치 우선순위: props > member 위치
    const latitude = initialLatitude ?? member?.latitude;
    const longitude = initialLongitude ?? member?.longitude;

    // 부모 컴포넌트에서 호출할 수 있는 메서드 노출
    useImperativeHandle(ref, () => ({
        getCurrentPosition: () => {
            if (mapInstanceRef.current) {
                const center = mapInstanceRef.current.getCenter();
                return {
                    latitude: center.getLat(),
                    longitude: center.getLng()
                };
            }
            return {
                latitude: latitude,
                longitude: longitude
            };
        },
        setCenter: (lat, lng) => {
            if (mapInstanceRef.current) {
                const newCenter = new window.kakao.maps.LatLng(lat, lng);
                mapInstanceRef.current.setCenter(newCenter);
            }
        }
    }));

    useEffect(() => {
        if (window.kakao && window.kakao.maps && mapRef.current && latitude && longitude) {
            const options = {
                center: new window.kakao.maps.LatLng(latitude, longitude),
                level: 3
            };

            try {
                const kakaoMap = new window.kakao.maps.Map(mapRef.current, options);
                mapInstanceRef.current = kakaoMap;
            } catch (error) {
                console.error('Dynamic map creation failed:', error);
            }
        }
    }, [latitude, longitude]);

    // 카카오맵 API가 로드되지 않은 경우 fallback UI
    if (!window.kakao || !window.kakao.maps) {
        return (
            <div className={fallbackClassName}>
                <IoLocation size={48} className="text-gray-400" />
                <p className="text-gray-500 mt-2">지도를 불러오는 중...</p>
            </div>
        );
    }

    return (
        <div className={`relative ${className}`}>
            <div 
                ref={mapRef} 
                className="w-full h-full"
                style={{ minHeight: '300px' }}
            />
            
            {/* 중앙 고정 핀 */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-6 pointer-events-none z-10">
                <IoLocation size={48} className="text-red-500 drop-shadow-lg" />
            </div>
        </div>
    );
});

DynamicKakaoMapSelectContainer.displayName = 'DynamicKakaoMapSelectContainer';

export default DynamicKakaoMapSelectContainer; 